import type { LoadHeaderDataResponse } from '@portal/processes/Content/loadHeaderData';
import { createCtx } from '@portal/shared/hooks/createCtx';
import { useLocale } from '@portal/shared/hooks/useLocale';
import { useStatsigIsReady } from '@portal/shared/hooks/useStatsigIsReady';
import { fetcher } from '@portal/shared/utils/fetcher';
import { useExperiment } from '@statsig/react-bindings';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { getQueryKeyForStudio } from '../utils/getQueryKeyForStudio';

/**
 * Used as a fallback for `getStaticProps` header data returns.
 *
 * Components that need header data independently of their parent route should
 * use {@link useHeaderContext}.
 */
function useQueryGetHeaderData() {
  const locale = useLocale();

  return useQuery({
    queryKey: getQueryKeyForStudio({ keys: ['header', locale] }),
    queryFn: () =>
      fetcher.get<LoadHeaderDataResponse>(`/api/header?locale=${locale}`).then((res) => res.data),
    staleTime: Infinity
  });
}

// Pre-load all experiments for headers we're expecting so their values are
// available as soon as possible.
export const HEADER_EXPERIMENT_KEYS = [
  // Manually defined to allow testing in dev, staging, and vercel preview
  // links.
  'test-header-experiment'
] as const;

type HeaderExperimentKey = (typeof HEADER_EXPERIMENT_KEYS)[number];
type ExperimentsRecord<T extends string = string> = {
  [key in T]?: { value: boolean; isLoading: boolean };
};

const getIsHeaderExperimentsBootstrapped = (entries: ExperimentsRecord) => {
  return (
    !HEADER_EXPERIMENT_KEYS.length ||
    HEADER_EXPERIMENT_KEYS.every((key) => !entries[key]?.isLoading)
  );
};

type DefaultHeader = LoadHeaderDataResponse['defaultHeader'];
export type HeaderContextData<Required = true> = {
  header: Required extends true ? DefaultHeader : DefaultHeader | null;
  isBootstrapped: boolean;
  logoSVG: LoadHeaderDataResponse['logoSVG'];
};

type UseHeaderArgs = { initialHeaderData: LoadHeaderDataResponse | null | undefined };
const useHeader = ({ initialHeaderData }: UseHeaderArgs) => {
  const statsigIsReady = useStatsigIsReady();

  // To ensure all usages of `useHeaderContext` have access to the header data,
  // regardless of what their parent route may be fetching, we should send a
  // request to get some initial header data.
  const { data: fallbackHeaderData } = useQueryGetHeaderData();
  const didSetFallbackHeaderData = useRef(false);

  const [headerOptions, setHeaderOptions] = useState(
    initialHeaderData || fallbackHeaderData || null
  );

  useEffect(() => {
    if (!didSetFallbackHeaderData.current && fallbackHeaderData) {
      didSetFallbackHeaderData.current = true;
      // Use the previous value if we already have it through some route fetch.
      setHeaderOptions((prev) => prev || fallbackHeaderData);
    }
  }, [fallbackHeaderData]);

  // Use experiments with exposure logging disabled since the middleware already logs exposures
  const testExperiment = useExperiment('test-header-experiment', { disableExposureLog: true });

  const headerExperimentEntries = Object.fromEntries([
    [
      'test-header-experiment',
      {
        value: testExperiment.get('enabled', false),
        isLoading: !statsigIsReady
      }
    ]
  ]) as ExperimentsRecord<HeaderExperimentKey>;

  // headerExperiments can't be memo'd because it's a record of hooks. Stringify it
  // for use as a dependency instead so we know when it changes.
  const headerExperimentsString = JSON.stringify(headerExperimentEntries);
  const headerExperiments = useMemo<typeof headerExperimentEntries>(
    () => JSON.parse(headerExperimentsString),
    [headerExperimentsString]
  );

  const [headerData, setHeaderData] = useState<HeaderContextData<false>>(() => {
    const experiments = headerOptions?.headerExperiments;
    return {
      header: headerOptions?.defaultHeader || null,
      isBootstrapped:
        experiments?.length === 0 || getIsHeaderExperimentsBootstrapped(headerExperiments),
      logoSVG: headerOptions?.logoSVG || undefined
    };
  });

  const chooseHeaderData = useCallback(
    ({
      options: { defaultHeader, headerExperiments: sanityHeaderExperiments, logoSVG },
      experiments
    }: {
      options: LoadHeaderDataResponse;
      experiments: ExperimentsRecord;
    }) => {
      setHeaderData((prev) => {
        const isBootstrapped =
          prev?.isBootstrapped || getIsHeaderExperimentsBootstrapped(experiments);
        const chosenHeaderExperiment = isBootstrapped
          ? sanityHeaderExperiments.find((i) => experiments[i.experimentName]?.value)
          : null;

        let header: DefaultHeader | null = null;
        if (chosenHeaderExperiment) {
          // Convert the header experiment to a normal header.
          header = {
            ...chosenHeaderExperiment,
            _type: 'header'
          };
        }
        header = header || defaultHeader || null;
        const newLogoSVG = logoSVG || prev.logoSVG;

        // Compare our new header data with the previous state and only update
        // if it's changed.
        if (
          prev.header === header &&
          prev.isBootstrapped === isBootstrapped &&
          prev.logoSVG === newLogoSVG
        ) {
          return prev;
        }

        return {
          header,
          isBootstrapped,
          logoSVG: newLogoSVG
        };
      });
    },
    []
  );

  // Watch both experiments and header options and choose the correct header for our
  // active experiment values.
  useEffect(() => {
    if (!headerOptions) {
      return;
    }
    chooseHeaderData({ options: headerOptions, experiments: headerExperiments });
  }, [chooseHeaderData, headerExperiments, headerOptions]);

  const getHeaderDataDefaults = useCallback(
    <T extends Pick<LoadHeaderDataResponse, 'defaultHeader' | 'logoSVG'>>(defaults: T) => {
      return {
        ...headerData,
        header: headerData.header || defaults?.defaultHeader,
        logoSVG: headerData.logoSVG || defaults?.logoSVG || undefined
      } satisfies typeof headerData;
    },
    [headerData]
  );

  const useSetHeaderOptions = (options: LoadHeaderDataResponse) => {
    useEffect(() => {
      setHeaderOptions(options);
    }, [options]);
  };

  return {
    headerData,
    getHeaderDataDefaults,
    setHeaderOptions,
    useSetHeaderOptions
  };
};

const [useHeaderContext, HeaderProvider] = createCtx<ReturnType<typeof useHeader>>('Header');
export { useHeaderContext };

export const HeaderProviderManager: React.FC<{ children: ReactNode } & UseHeaderArgs> = ({
  initialHeaderData,
  children
}) => {
  const value = useHeader({ initialHeaderData });
  return <HeaderProvider value={value}>{children}</HeaderProvider>;
};
